import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchFiles } from "../redux/actions/fileActions";
import { fetchRelationships } from "../redux/actions/relationshipActions";
import { useNavigate } from "react-router-dom";

import SearchIcon from "@mui/icons-material/Search";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import FilterListIcon from "@mui/icons-material/FilterList";
import PersonIcon from "@mui/icons-material/Person";
import CategoryIcon from "@mui/icons-material/Category";
import AssignmentIcon from "@mui/icons-material/Assignment";
// import DeleteIcon from '@material-ui/icons/Delete';
// import EditIcon from '@material-ui/icons/Edit';
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import NoteIcon from "@mui/icons-material/Note";
import NoteOutlinedIcon from "@mui/icons-material/NoteOutlined";
// import AzureAuthenticator from "../components/TestComp";
import AzureAuthenticator from "../components/TestComp";
import FolderOpenOutlinedIcon from "@mui/icons-material/FolderOpenOutlined";

import {
  Snackbar,
  SnackbarContent,
  TableSortLabel,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Button,
  Container,
  TextField,
  Grid,
  Box,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TablePagination,
  Breadcrumbs,
  Typography,
  Link,
  Autocomplete,
  Avatar,
  Stack,
  InputAdornment,
  CircularProgress,
  TableContainer,
  FormControl,
} from "@mui/material";

import axios from "../utils/axiosConfig";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import { actionButtonStyles } from "../styles/actionButton";
import BreadcrumbNav from "../components/BreadcrumbNav";
import { textFieldStyles } from "../styles/textFieldStyles";
import { CustomPaper } from "../styles/customPaper";
import { windowsTableStyles } from "../styles/windowsTableStyles";
import NotesDialog from "../components/NotesDialog";
import CustomAutocomplete from "../components/CustomAutocomplete";
import { smallTitle } from "../styles/typography";
import { useTranslation } from 'react-i18next';
import { createStatusTranslator, createTypeTranslator } from '../utils/statusTranslation';
import { formatDate } from "../utils/dateUtils";
const tableStyles = windowsTableStyles();

const buttonStyles = {
  borderRadius: 1,
  padding: "6px 16px",
  textTransform: "none",
  boxShadow: "none",
  height: "fit-content",
  "&:hover": {
    boxShadow: "0px 3px 6px rgba(0, 0, 0, 0.1)",
  },
};

const tableHeaderStyles = {
  backgroundColor: "#f5f5f5",
  color: "#333",
  fontWeight: "bold",
  letterSpacing: "0.1em",
  padding: "10px 15px",
};

const FilesPage = () => {
  const { t } = useTranslation(['pages', 'common', 'buttons', 'forms', 'navigation']);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Create translators using shared utilities
  const translateStatus = createStatusTranslator(t);
  const translateType = createTypeTranslator(t);
  const { files, loading, error } = useSelector((state) => state.fileList);
  const { relationships } = useSelector((state) => state.relationshipList);

  const [order, setOrder] = useState("asc");
  const [orderBy, setOrderBy] = useState("");

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(100);
  const [lawyerFilter, setLawyerFilter] = useState(null);
  const [lawyerFilterCleared, setLawyerFilterCleared] = useState(false);
  const [clientFilter, setClientFilter] = useState(null);
  const [typeFilter, setTypeFilter] = useState("");

  const [statusFilter, setStatusFilter] = useState("Active");
  const [searchFilter, setSearchFilter] = useState("");
  const [user, setUser] = useState(null);

  const [selectedFile, setSelectedFile] = useState(null);
  const [notes, setNotes] = useState([]);
  const [newNote, setNewNote] = useState("");
  const [newNoteDate, setNewNoteDate] = useState("");
  const [showNewNoteField, setShowNewNoteField] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success"); // Add severity state

  useEffect(() => {
    // Only fetch if not already loaded to avoid unnecessary API calls
    if (!files || files.length === 0) {
      dispatch(fetchFiles());
    }
    if (!relationships || relationships.length === 0) {
      dispatch(fetchRelationships());
    }
    const fetchUser = async () => {
      const token = localStorage.getItem("token");
      const config = {
        headers: {
          "x-auth-token": token,
        },
      };
      const response = await axios.get("/api/auth/", config);
      const user = response.data;
      console.log("User:", user);
      setUser(response.data);
    };

    fetchUser();
  }, [dispatch, files, relationships]);

  // Auto-populate filter for all users (including Managers)
  useEffect(() => {
    if (user && relationships?.length > 0 && !lawyerFilter && !lawyerFilterCleared) {
      // Find the user's relationship in the relationships array
      const userRelationship = relationships?.find(
        (rel) => rel._id === user.relationship && rel.type === "Lawyer"
      );
      if (userRelationship) {
        setLawyerFilter(userRelationship);
      }
    }
  }, [user, relationships, lawyerFilter, lawyerFilterCleared]);

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleCreateFile = () => {
    navigate("/files/create");
  };

  const statusOptions = [
    { value: "Active", label: t('common:active') },
    { value: "Inactive", label: t('common:inactive') }
  ];

  const filteredFiles = (files || []).filter((file) => {
    const responsibleLawyer = file?.officeMembers?.find(
      (member) => member.role === "Responsible Lawyer"
    );
    const treatingLawyer = file?.officeMembers?.find(
      (member) => member.role === "Treating Lawyer"
    );
    const client = file?.parties.find((party) => party.type === "Client");
    const payingParty = file.parties.find(
      (party) => party.type === "Paying Party"
    );

    const clientName = client
      ? client?.name?.contractType === "Individual"
        ? `${client.name.firstName} ${
            client.name.middleName ? client.name.middleName + " " : ""
          }${client.name.lastName}`
        : client.name.businessName
      : "";

    const payingPartyName = payingParty
      ? payingParty?.name?.contractType === "Individual"
        ? `${payingParty.name.firstName} ${
            payingParty.name.middleName ? payingParty.name.middleName + " " : ""
          }${payingParty.name.lastName}`
        : payingParty.name.businessName
      : "";

    return (
      (lawyerFilter
        ? (responsibleLawyer && responsibleLawyer.member._id === lawyerFilter._id) ||
          (treatingLawyer && treatingLawyer.member._id === lawyerFilter._id)
        : true) &&
      (clientFilter
        ? client &&
          `${client.name.uniqueNumber ? client.name.uniqueNumber + " - " : ""}${
            client.name.contractType === "Individual"
              ? `${client.name.firstName} ${
                  client.name.middleName ? client.name.middleName + " " : ""
                }${client.name.lastName}`
              : client.name.businessName
          }`
            .toLowerCase()
            .includes(clientFilter.label.toLowerCase())
        : true) &&
      (typeFilter ? file.type.includes(typeFilter) : true) &&
      (statusFilter
        ? file.status.toLowerCase() === statusFilter.toLowerCase()
        : true) &&
      (searchFilter
        ? file.name?.toLowerCase().includes(searchFilter.toLowerCase()) ||
          (responsibleLawyer &&
            `${
              responsibleLawyer.member?.uniqueNumber
                ? responsibleLawyer.member.uniqueNumber + " - "
                : ""
            }${responsibleLawyer.member?.firstName || ""} ${
              responsibleLawyer.member?.middleName
                ? responsibleLawyer.member.middleName + " "
                : ""
            }${responsibleLawyer.member?.lastName || ""}`
              .toLowerCase()
              .includes(searchFilter.toLowerCase())) ||
          (treatingLawyer &&
            `${
              treatingLawyer.member?.uniqueNumber
                ? treatingLawyer.member.uniqueNumber + " - "
                : ""
            }${treatingLawyer.member?.firstName || ""} ${
              treatingLawyer.member?.middleName
                ? treatingLawyer.member.middleName + " "
                : ""
            }${treatingLawyer.member?.lastName || ""}`
              .toLowerCase()
              .includes(searchFilter.toLowerCase())) ||
          clientName?.toLowerCase().includes(searchFilter.toLowerCase()) ||
          file.fileNo?.toLowerCase().includes(searchFilter.toLowerCase()) ||
          file.type?.toLowerCase().includes(searchFilter.toLowerCase()) ||
          file.status?.toLowerCase().includes(searchFilter.toLowerCase()) ||
          payingPartyName?.toLowerCase().includes(searchFilter.toLowerCase())
        : true)
    );
  });

  const handleOpenNotes = async (file) => {
    try {
      const response = await axios.get(`/api/files/${file._id}`);
      setSelectedFile(response.data);
      setNotes(response.data.notes);
    } catch (error) {
      console.error("Error fetching notes:", error);
    }
  };

  const handleCloseNotes = () => {
    setSelectedFile(null);
    setNotes([]);
    setNewNote("");
    setNewNoteDate("");
    setShowNewNoteField(false);
  };

  const handleAddNote = () => {
    setNewNoteDate(formatDate(new Date()));
    setShowNewNoteField(true);
  };
  const handleSaveAllNotes = async () => {
    try {
      const updatePromises = (notes || []).map((note) =>
        axios.put(
          `/api/files/${selectedFile._id}/notes/${note._id}`,
          {
            content: note.content,
          },
          {
            headers: { "Content-Type": "application/json" },
          }
        )
      );

      if (newNote) {
        updatePromises.push(
          axios.post(
            `/api/files/${selectedFile._id}/notes`,
            {
              content: newNote,
              date: new Date(),
            },
            {
              headers: { "Content-Type": "application/json" },
            }
          )
        );
      }
      await Promise.all(updatePromises);
      const response = await axios.get(`/api/files/${selectedFile._id}`);
      const updatedFile = response.data;
      setNotes(updatedFile.notes);
      setNewNote("");
      setNewNoteDate("");
      setShowNewNoteField(false);
      setSnackbarMessage(t('pages:files.notes_saved_successfully'));
      setSnackbarOpen(true);
    } catch (error) {
      console.error("Error saving notes:", error);
    }
  };

  const handleDeleteNote = async (noteId) => {
    try {
      const response = await axios.delete(
        `/api/files/${selectedFile._id}/notes/${noteId}`
      );
      const updatedFile = response.data;
      setNotes(updatedFile.notes);
    } catch (error) {
      console.error("Error deleting note:", error);
    }
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const lawyerOptions = [
    ...new Map(
      (files || [])
        .map((file) => {
          const responsibleLawyer = file.officeMembers.find(
            (member) => member.role === "Responsible Lawyer"
          );
          const treatingLawyer = file.officeMembers.find(
            (member) => member.role === "Treating Lawyer"
          );
          return [
            responsibleLawyer
              ? {
                  label: `${
                    responsibleLawyer.member.uniqueNumber
                      ? responsibleLawyer.member.uniqueNumber + " - "
                      : ""
                  }${responsibleLawyer.member.firstName} ${
                    responsibleLawyer.member.middleName
                      ? responsibleLawyer.member.middleName + " "
                      : ""
                  }${responsibleLawyer.member.lastName}`,
                  value: responsibleLawyer.member.uniqueNumber,
                }
              : null,
            treatingLawyer
              ? {
                  label: `${
                    treatingLawyer.member.uniqueNumber
                      ? treatingLawyer.member.uniqueNumber + " - "
                      : ""
                  }${treatingLawyer.member.firstName} ${
                    treatingLawyer.member.middleName
                      ? treatingLawyer.member.middleName + " "
                      : ""
                  }${treatingLawyer.member.lastName}`,
                  value: treatingLawyer.member.uniqueNumber,
                }
              : null,
          ];
        })
        .flat()
        .filter(Boolean)
        .map((item) => [item.value, item])
    ).values(),
  ];

  const clientOptions = [
    ...new Map(
      (files || [])
        .map((file) => {
          const client = file.parties.find((party) => party.type === "Client");
          return client
            ? {
                label: `${
                  client.name.uniqueNumber
                    ? client.name.uniqueNumber + " - "
                    : ""
                }${
                  client.name.contractType === "Individual"
                    ? `${client.name.firstName} ${
                        client.name.middleName
                          ? client.name.middleName + " "
                          : ""
                      }${client.name.lastName}`
                    : client.name.businessName
                }`,
                value: client.name.uniqueNumber,
              }
            : null;
        })
        .filter(Boolean)
        .map((item) => [item.value, item])
    ).values(),
  ];

  const typeOptions = [
    { value: "Liability Law", label: t('pages:files.types.liability_law') },
    { value: "Civil Law", label: t('pages:files.types.civil_law') },
    { value: "Labor Law", label: t('pages:files.types.labor_law') },
    { value: "Administrative Law", label: t('pages:files.types.administrative_law') },
    { value: "Tax Law", label: t('pages:files.types.tax_law') },
    { value: "Construction Law", label: t('pages:files.types.construction_law') },
    { value: "Rental & Residential Law", label: t('pages:files.types.rental_residential_law') },
    { value: "Environment & Spatial Planning Law", label: t('pages:files.types.environment_spatial_law') },
    { value: "Corporate Law", label: t('pages:files.types.corporate_law') },
    { value: "Military Law", label: t('pages:files.types.military_law') },
    { value: "Education Law", label: t('pages:files.types.education_law') },
    { value: "Other", label: t('pages:files.types.other') },
    { value: "Personal & Family Law", label: t('pages:files.types.personal_family_law') },
    { value: "Legal Entity Law", label: t('pages:files.types.legal_entity_law') },
    { value: "Social Security Law", label: t('pages:files.types.social_security_law') },
    { value: "Criminal Law", label: t('pages:files.types.criminal_law') },
    { value: "Diciplinary Law", label: t('pages:files.types.disciplinary_law') },
    { value: "Law of Obligations", label: t('pages:files.types.law_obligations') },
    { value: "Insurance Law", label: t('pages:files.types.insurance_law') },
    { value: "Immigration Law", label: t('pages:files.types.immigration_law') },
    { value: "Commercial Law", label: t('pages:files.types.commercial_law') },
  ];

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const sortedFiles = (filteredFiles || []).sort((a, b) => {
    if (orderBy) {
      let aValue = a[orderBy];
      let bValue = b[orderBy];

      if (orderBy === "responsibleLawyer") {
        aValue =
          a.officeMembers.find((member) => member.role === "Responsible Lawyer")
            ?.member?.firstName || "";
        bValue =
          b.officeMembers.find((member) => member.role === "Responsible Lawyer")
            ?.member?.firstName || "";
      } else if (orderBy === "treatingLawyer") {
        aValue =
          a.officeMembers.find((member) => member.role === "Treating Lawyer")
            ?.member?.firstName || "";
        bValue =
          b.officeMembers.find((member) => member.role === "Treating Lawyer")
            ?.member?.firstName || "";
      } else if (orderBy === "clientName") {
        aValue =
          a.parties.find((party) => party.type === "Client")?.name?.firstName ||
          "";
        bValue =
          b.parties.find((party) => party.type === "Client")?.name?.firstName ||
          "";
      }

      if (order === "asc") {
        return aValue < bValue ? -1 : 1;
      } else {
        return aValue > bValue ? -1 : 1;
      }
    }
    return 0;
  });
  // if (loading) {
  //   return (
  //     <Box sx={{ display: 'flex', justifyContent: 'center', padding: '32px 24px' }}>
  //       <CircularProgress />
  //     </Box>
  //   );
  // }

  if (error) {
    return <div>Error: {error}</div>;
  }
  return (
    <Container
      maxWidth="lg"
      sx={{
        backgroundColor: "white",
        boxShadow: "0px 4px 16px rgba(0,0,0,0.013)", // Reduced shadow spread
        pt: 2,
        marginBottom: 8,
        marginTop: 2,
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "0",
          margin: "0",
          mb: 4,
          mt: 2,
          gap: 2,
        }}
      >
        <BreadcrumbNav title={t('pages:files.title')} />

        <Button
          startIcon={<AddIcon />}
          onClick={() => navigate("/files/create")}
          size="small"
          sx={actionButtonStyles()}
        >
          {t('pages:files.create_new')}
        </Button>
      </Box>

      <Grid container spacing={3} alignItems="center">
        <Grid item xs={12}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              marginBottom: 2,
              width: "100%",
            }}
          >
            <TextField
              label={t('common:searchAnything')}
              value={searchFilter}
              onChange={(e) => setSearchFilter(e.target.value)}
              InputLabelProps={{
                shrink: true,
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
                sx: {
                  // further adjustments to inner padding
                  padding: "0 10px",
                  boxShadow: "none",
                },
              }}
              size="small"
              sx={textFieldStyles({ width: "20%", marginRight: "0" })}
            />

            <FormControl sx={{ width: "20%" }} size="small">
              <CustomAutocomplete
                options={relationships?.filter((rel) => rel.type === "Lawyer").reduce((acc, current) => {
                  // Deduplicate by _id to prevent duplicate entries
                  if (!acc.find(item => item._id === current._id)) {
                    acc.push(current);
                  }
                  return acc;
                }, []) || []}
                value={lawyerFilter}
                onChange={(event, newValue) => {
                  setLawyerFilter(newValue);
                  // Track if filter was manually cleared
                  if (newValue === null) {
                    setLawyerFilterCleared(true);
                  } else {
                    setLawyerFilterCleared(false);
                  }
                }}
                label={t('pages:files.filter_by_lawyer')}
                filterType="Member"
                addNewLabel={t('pages:files.add_new_identity')}
                addNewPath="/relationships/create"
                showIcon={true}
                icon={FilterListIcon}
                showAddNewOption={false}
              />
            </FormControl>
            <Autocomplete
              options={clientOptions}
              getOptionLabel={(option) => option.label}
              value={clientFilter}
              onChange={(event, newValue) => setClientFilter(newValue)}
              sx={{ width: "20%" }}
              PaperComponent={CustomPaper}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t('pages:files.filter_by_client')}
                  size="small"
                  sx={textFieldStyles({ marginRight: "0" })}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <InputAdornment position="start">
                        <PersonIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              )}
              renderOption={(props, option) => (
                <li {...props}>{option.label}</li>
              )}
            />
            <Autocomplete
              options={typeOptions}
              getOptionLabel={(option) => option.label || option}
              value={typeOptions.find(opt => opt.value === typeFilter) || null}
              onChange={(event, newValue) => setTypeFilter(newValue?.value || "")}
              sx={{ width: "20%" }}
              PaperComponent={CustomPaper}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t('pages:files.filter_by_type')}
                  size="small"
                  sx={textFieldStyles({ marginRight: "0" })}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <InputAdornment position="start">
                        <CategoryIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              )}
            />
            <Autocomplete
              options={statusOptions}
              getOptionLabel={(option) => option.label || option}
              value={statusOptions.find(opt => opt.value === statusFilter) || null}
              onChange={(event, newValue) => setStatusFilter(newValue?.value || "")}
              sx={{ width: "20%" }}
              PaperComponent={CustomPaper}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t('pages:files.filter_by_status')}
                  size="small"
                  sx={textFieldStyles({ marginRight: "0" })}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <InputAdornment position="start">
                        <AssignmentIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              )}
            />
          </Box>
        </Grid>
      </Grid>
      {loading ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            padding: "32px 24px",
          }}
        >
          <CircularProgress />
        </Box>
      ) : (
        <Box>
          {filteredFiles.length > 0 ? (
            <TableContainer>
              <Table sx={tableStyles.container} size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={tableStyles.headerCell}>#</TableCell>
                    <TableCell sx={tableStyles.headerCell}>
                      <TableSortLabel
                        active={orderBy === "fileNo"}
                        direction={orderBy === "fileNo" ? order : "asc"}
                        onClick={() => handleRequestSort("fileNo")}
                      >
                        {t('pages:files.file_number')}
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={tableStyles.headerCell}>
                      <TableSortLabel
                        active={orderBy === "name"}
                        direction={orderBy === "name" ? order : "asc"}
                        onClick={() => handleRequestSort("name")}
                      >
                        {t('pages:files.file_name')}
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={tableStyles.headerCell}>
                      <TableSortLabel
                        active={orderBy === "responsibleLawyer"}
                        direction={
                          orderBy === "responsibleLawyer" ? order : "asc"
                        }
                        onClick={() => handleRequestSort("responsibleLawyer")}
                      >
                        {t('pages:files.responsible_lawyer')}
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={tableStyles.headerCell}>
                      <TableSortLabel
                        active={orderBy === "treatingLawyer"}
                        direction={orderBy === "treatingLawyer" ? order : "asc"}
                        onClick={() => handleRequestSort("treatingLawyer")}
                      >
                        {t('pages:files.treating_lawyer')}
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={tableStyles.headerCell}>
                      <TableSortLabel
                        active={orderBy === "clientName"}
                        direction={orderBy === "clientName" ? order : "asc"}
                        onClick={() => handleRequestSort("clientName")}
                      >
                        {t('pages:files.client_name')}
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={tableStyles.headerCell}>
                      <TableSortLabel
                        active={orderBy === "type"}
                        direction={orderBy === "type" ? order : "asc"}
                        onClick={() => handleRequestSort("type")}
                      >
                        {t('pages:files.jurisdiction')}
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={tableStyles.headerCell}>
                      <TableSortLabel
                        active={orderBy === "status"}
                        direction={orderBy === "status" ? order : "asc"}
                        onClick={() => handleRequestSort("status")}
                      >
                        {t('forms:labels.status')}
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={tableStyles.headerCell}>{t('pages:files.notes')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedFiles
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((file, index) => (
                      <TableRow key={file._id} sx={{ ...tableStyles.row }}>
                        <TableCell sx={tableStyles.cell}>
                          {page * rowsPerPage + index + 1}
                        </TableCell>
                        <TableCell
                          onClick={() =>
                            navigate(`/files/${file._id}/overview`)
                          }
                          sx={{
                            ...tableStyles.cell,
                            cursor: "pointer",
                            "&:hover": {
                              textDecoration: "underline !important",
                            },
                            color: "#005b92",
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 0.5,
                            }}
                          >
                            <FolderOpenOutlinedIcon
                              sx={{ fontSize: "1rem", color: "#005b92" }}
                            />
                            <span style={{ marginTop: "1px" }}>
                              {file.fileNo}
                            </span>
                          </Box>
                        </TableCell>

                        <TableCell
                          onClick={() =>
                            navigate(`/files/${file._id}/overview`)
                          }
                          sx={{
                            ...tableStyles.cell,
                            // padding: "8px",
                            fontSize: "10.5px",
                            cursor: "pointer",
                            "&:hover": {
                              textDecoration: "underline !important",
                              cursor: "pointer",
                            },
                            color: "#005b92",
                            minHeight: "10px 1important",
                          }}
                        >
                          {file.name}
                        </TableCell>

                        <TableCell
                          sx={{
                            ...tableStyles.cell,
                          }}
                          // onClick={() => navigate(`/relationships/${file.officeMembers.find(member => member.role === "Responsible Lawyer").member?._id}`)}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <Avatar
                              alt={
                                file.officeMembers.find(
                                  (member) =>
                                    member.role === "Responsible Lawyer"
                                )?.member?.firstName
                              }
                              src={
                                file.officeMembers.find(
                                  (member) =>
                                    member.role === "Responsible Lawyer"
                                )?.member?.avatar
                                  ? file.officeMembers.find(
                                      (member) =>
                                        member.role === "Responsible Lawyer"
                                    )?.member?.avatar
                                  : "/static/images/avatar/1.jpg"
                              }
                              style={{
                                width: "16px",
                                height: "16px",
                              }}
                            />
                            <Box
                              sx={{
                                // color: "rgb(53, 53, 53)",
                                // color: "#005b92",
                                display: "block",
                                fontFamily:
                                  '"Fira Sans", "Helvetica Neue", Helvetica, Roboto, Arial, sans-serif',
                                fontSize: "10.5px",
                                fontWeight: 400,
                                height: "13.5px",
                                lineHeight: "13.5px",
                                marginLeft: "-3px",
                              }}
                            >
                              {file.officeMembers.find(
                                (member) => member.role === "Responsible Lawyer"
                              )
                                ? (() => {
                                    const responsibleLawyer =
                                      file.officeMembers.find(
                                        (member) =>
                                          member.role === "Responsible Lawyer"
                                      )?.member;
                                    return `${responsibleLawyer.firstName} ${
                                      responsibleLawyer.middleName
                                        ? responsibleLawyer.middleName + " "
                                        : ""
                                    }${responsibleLawyer.lastName}`;
                                  })()
                                : "N/A"}
                            </Box>
                          </Box>
                        </TableCell>

                        <TableCell sx={tableStyles.cell}>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <Avatar
                              alt={
                                file?.officeMembers?.find(
                                  (member) =>
                                    member?.role === "Treating Lawyer"
                                )?.member?.firstName
                              }
                              src={
                                file?.officeMembers?.find(
                                  (member) =>
                                    member?.role === "Treating Lawyer"
                                )?.member?.avatar
                                  ? file.officeMembers.find(
                                      (member) =>
                                        member.role === "Treating Lawyer"
                                    )?.member?.avatar
                                  : "/static/images/avatar/1.jpg"
                              }
                              style={{
                                width: "16px",
                                height: "16px",
                              }}
                            />
                            <Box
                              sx={{
                                // color: "rgb(53, 53, 53)",
                                // color: "#005b92",
                                display: "block",
                                fontFamily:
                                  '"Fira Sans", "Helvetica Neue", Helvetica, Roboto, Arial, sans-serif',
                                fontSize: "10.5px",
                                fontWeight: 400,
                                height: "13.5px",
                                lineHeight: "13.5px",
                                marginLeft: "-3px",
                              }}
                            >
                              {file?.officeMembers.find(
                                (member) => member?.role === "Treating Lawyer"
                              )
                                ? (() => {
                                    const treatingLawyer =
                                      file.officeMembers.find(
                                        (member) =>
                                          member?.role === "Treating Lawyer"
                                      )?.member;
                                    return `${
                                      treatingLawyer.firstName
                                    } ${
                                      treatingLawyer.middleName
                                        ? treatingLawyer.middleName + " "
                                        : ""
                                    }${treatingLawyer.lastName}`;
                                  })()
                                : "N/A"}
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell
                          sx={{
                            ...tableStyles.cell,
                            cursor: "pointer",
                          }}
                          onClick={() =>
                            navigate(
                              `/relationships/${
                                file.parties.find(
                                  (member) => member.role === "Client"
                                )?.member?._id
                              }`
                            )
                          }
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <Avatar
                              alt={
                                file.parties.find(
                                  (party) => party.type === "Client"
                                )?.name?.businessName || "Client"
                              }
                              src={
                                file.parties.find(
                                  (party) => party.type === "Client"
                                )?.name?.avatar || "/static/images/avatar/1.jpg"
                              }
                              sx={{
                                width: "16px",
                                height: "16px",
                                fontSize: "7.8px",
                              }}
                            />
                            {console.log(
                              "avatar is",
                              file.parties.find(
                                (party) => party.type === "Client"
                              )?.member?.avatar
                            )}
                            <Box
                              sx={{
                                // color: "rgb(53, 53, 53)",
                                color: "#005b92",
                                display: "block",
                                height: "13.5px",
                                lineHeight: "13.5px",
                                "&:hover": {
                                  textDecoration: "underline !important",
                                  cursor: "pointer",
                                },
                                marginLeft: "-3px",
                              }}
                            >
                              {file.parties.find(
                                (party) => party.type === "Client"
                              )
                                ? (() => {
                                    const client = file.parties.find(
                                      (party) => party.type === "Client"
                                    ).name;
                                    if (client.contractType === "Individual") {
                                      return `${client.firstName} ${
                                        client.middleName
                                          ? client.middleName + " "
                                          : ""
                                      }${client.lastName}`;
                                    } else if (
                                      client.contractType === "Company"
                                    ) {
                                      return `${client.businessName}`;
                                    } else {
                                      return "N/A";
                                    }
                                  })()
                                : "N/A"}
                            </Box>
                          </Box>
                        </TableCell>

                        <TableCell sx={tableStyles.cell}>{translateType(file.type)}</TableCell>
                        <TableCell
                          sx={{
                            ...tableStyles.cell,
                            padding: "4px",
                            fontSize: "10.5px",
                          }}
                        >
                          <span
                            style={{
                              padding: "1px 6px",
                              borderRadius: "20px",
                              backgroundColor:
                                file.status === "Active"
                                  ? "#85bc20"
                                  : file.status === "Inactive"
                                  ? "#e5202e"
                                  : "transparent",
                              color:
                                file.status === "Inactive" ? "white" : "black",
                              fontSize: "8.25px",
                              fontFamily:
                                '"Fira Sans", "Helvetica Neue", Helvetica, Roboto, Arial, sans-serif',
                              fontWeight: "700",
                              textTransform: "upperCase",
                              height: "12px",
                              letterSpacing: "0.2em",
                              // display: "inline-block",
                            }}
                          >
                            {translateStatus(file.status)}
                          </span>
                        </TableCell>

                        <TableCell sx={tableStyles.cell}>
                          <IconButton
                            onClick={() => handleOpenNotes(file)}
                            sx={{ width: "16px", height: "16px", padding: 0 }}
                          >
                            {file.notes && file.notes.length > 0 ? (
                              <NoteIcon sx={{ fontSize: "20px" }} />
                            ) : (
                              <NoteOutlinedIcon sx={{ fontSize: "20px" }} />
                            )}
                          </IconButton>
                          <NotesDialog
                            selectedFile={selectedFile}
                            file={file}
                            notes={notes}
                            newNote={newNote}
                            newNoteDate={newNoteDate}
                            showNewNoteField={showNewNoteField}
                            snackbarOpen={snackbarOpen}
                            snackbarMessage={snackbarMessage}
                            handleCloseNotes={handleCloseNotes}
                            handleAddNote={handleAddNote}
                            handleSaveAllNotes={handleSaveAllNotes}
                            handleDeleteNote={handleDeleteNote}
                            setNewNote={setNewNote}
                            setShowNewNoteField={setShowNewNoteField}
                            setNotes={setNotes}
                            setSnackbarOpen={setSnackbarOpen}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
              <TablePagination
                component="div"
                count={filteredFiles.length}
                page={page}
                onPageChange={handlePageChange}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleRowsPerPageChange}
                labelRowsPerPage={t('common:rows_per_page')}
              />
            </TableContainer>
          ) : (
            <Box
              sx={{ ...smallTitle, padding: "32px 24px", textAlign: "center" }}
            >
              {t('pages:search.no_results')}
            </Box>
          )}
        </Box>
      )}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
      >
        <SnackbarContent
          message={
            <span style={{ display: "flex", alignItems: "center" }}>
              <CheckCircleIcon style={{ marginRight: "8px" }} />
              {snackbarMessage}
            </span>
          }
          style={{
            backgroundColor:
              snackbarSeverity === "success" ? "#d4edda" : "#f8d7da",
            color: snackbarSeverity === "success" ? "#155724" : "#721c24",
          }}
          action={
            <Button color="inherit" size="small" onClick={handleSnackbarClose}>
              {t('buttons:close')}
            </Button>
          }
        />
      </Snackbar>
    </Container>
  );
};

export default FilesPage;
